import { useState } from 'react';
import { Alert, Button, DetailSection, StatusBadge, useToast } from '@dru/ui';
import { api } from '../../../lib/api';
import { errorMessage } from '../../../lib/auth';

/**
 * Whether this partner provides emergency care through Dr Ultimate.
 *
 * A capability, separate from lab or hospital type: a lab with no casualty
 * stays off; a hospital, or a partner with both, can be switched on. Only
 * then do its logins get the emergency feature and can it be named as a
 * company's emergency provider. Off keeps past visits and bills.
 */
export function EmergencyProviderSwitch({ partnerId, enabled, onChanged }: {
  partnerId: string; enabled: boolean; onChanged: () => void;
}) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function flip() {
    setBusy(true); setError(null);
    try {
      await api.patch(`/admin/partner-assignments/partners/${partnerId}/emergency-provider`, { enabled: !enabled });
      toast('success', enabled ? 'Emergency provision switched off.' : 'Emergency provision switched on.');
      onChanged();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <DetailSection
      title="Emergency provider"
      action={<StatusBadge tone={enabled ? 'covered' : 'neutral'}>{enabled ? 'On' : 'Off'}</StatusBadge>}
    >
      <p className="text-small text-neutral-600">
        {enabled
          ? 'This partner treats employees in an emergency. Its users get Emergency visits, and companies can be given it as their emergency provider.'
          : 'This partner does not take emergency cases. Switch it on only if it has emergency facilities.'}
        {' '}Its users see the change when they next sign in.
      </p>
      {error && <Alert className="mt-2">{error}</Alert>}
      <Button className="mt-3 max-md:w-full" size="sm" variant={enabled ? 'secondary' : 'primary'} loading={busy} onClick={flip}>
        {enabled ? 'Switch off' : 'Switch on'}
      </Button>
    </DetailSection>
  );
}
